import { FC, useCallback, useRef, useState } from 'react';

import StepHeader from '@components/quiz/shared/StepHeader';
import CtaButton from '@components/quiz/shared/CtaButton';
import RatingGauge from '@components/quiz/shared/RatingGauge';
import TeamSearch from '@components/quiz/ReadinessSteps/shared/TeamSearch';
import { useGytr } from '@components/quiz/GytrProvider';
import type { FplEntrySearchResult } from '@api-service/fpl-entries';
import type { SanityGytrImportStepBlock } from '@lib/sanity/types';

type GytrImportStepProps = SanityGytrImportStepBlock & {
  loginUrl: string;
  onImported: () => void;
  /** A read that needs correcting before it can be rated (FFH-2738). */
  onNeedsReview: () => void;
  onBuildManually: () => void;
};

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg'];

const SEARCH_INPUT_ID = 'gytr-team-search';

/**
 * The re-tiered step's copy (FFH-2742).
 *
 * Hardcoded on purpose for this pass: the screen is being reviewed before eight
 * new fields and a content migration are committed to wording that may still
 * move. Moving these to `gytrImportStepBlock` is the follow-up, and the two
 * strings that already have fields (`searchPlaceholder`, `footnote`) are
 * deliberately ignored here rather than half-migrated, because the documents
 * still hold copy written for the old three-button layout.
 */
const SEARCH_COPY = {
  fieldLabel: 'Find your FPL team',
  placeholder: 'Team name or FPL ID',
  ctaText: 'Find my team',
  idHint: 'Your ID is the number in your FPL points page URL.',
  idHelpLink: "Where's mine?",
  idHelpBody:
    'Open the FPL site and go to Points. Your ID is the number in the address bar straight after /entry/.',
  idHelpLinkHref: 'https://fantasy.premierleague.com/',
  idHelpLinkLabel: 'Where to look on fantasy.premierleague.com',
  fallbackPrefix: "Can't find it? ",
  fallbackImport: 'Import a screenshot',
  fallbackSeparator: ' or ',
  fallbackBuild: 'build manually',
  fallbackSuffix: '.',
  noResultsCardTitle: 'Skip the search',
  noResultsCardBody:
    "Send a screenshot of your FPL squad and we'll read the 15 players off it.",
  teamUnavailableCardTitle: "FPL hasn't released your team yet",
  teamUnavailableCardBody:
    "FPL isn't serving team {fplId} at the moment, which happens around the gameweek rollover. Send a screenshot of your squad instead and we'll read the 15 players off it.",
  noResultsBuildLink: 'Or build your team manually',
  footnote: 'Win your mini-league or your money back.'
} as const;

// The ring is decorative, and shrinking it on the search-led layout is what
// buys the room the new field, hint and CTA need.
const SEARCH_LED_GAUGE_SIZE = 132;

/**
 * Step 1: team import.
 *
 * Pre-GW1 is value-first: an empty gauge ring as the payoff hint, screenshot
 * import as the primary purple callout, build-manually beneath it, and no FPL
 * search (the API has nothing to find before a gameweek has been played).
 *
 * Post-GW1 (`layoutVariant: 'search-first'`) is search-led (FFH-2742). Search
 * is the path the product wants taken, so it gets the label, the ID help and
 * the step's only primary CTA, while screenshot import and building by hand
 * collapse into one line of text. Screenshot import earns a card of its own
 * only once a search has come back empty, which is the moment it is actually
 * the better route.
 */
const GytrImportStep: FC<GytrImportStepProps> = ({
  header,
  layoutVariant,
  importButtonText,
  buildManuallyText,
  // `searchPlaceholder` is deliberately not read: the documents still hold the
  // placeholder written for the old layout, and this pass carries its own copy.
  searchSelectButtonText,
  proofLine,
  footnote,
  ocrReadErrorText,
  loginUrl,
  onImported,
  onNeedsReview,
  onBuildManually
}) => {
  const { state, actions } = useGytr();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isIdHelpOpen, setIsIdHelpOpen] = useState(false);
  const [hasEmptyResults, setHasEmptyResults] = useState(false);

  const handleFileSelect = useCallback(
    async (file: File) => {
      setFileError(null);

      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        setFileError('Please upload a PNG or JPG image.');
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileError('File must be under 10MB.');
        return;
      }

      const status = await actions.importTeamFromScreenshot(
        file,
        ocrReadErrorText
      );
      if (status === 'imported') onImported();
      if (status === 'needs-review') onNeedsReview();
    },
    [actions, ocrReadErrorText, onImported, onNeedsReview]
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) handleFileSelect(file);
      // Allow re-selecting the same file after a failed read
      event.target.value = '';
    },
    [handleFileSelect]
  );

  const handleEntrySelected = useCallback(
    async (entry: FplEntrySearchResult) => {
      await actions.importTeamByFplId(String(entry.fplEntryId));
      onImported();
    },
    [actions, onImported]
  );

  const openFilePicker = useCallback(() => fileInputRef.current?.click(), []);

  const inlineError = fileError ?? state.importError;
  const isSearchFirst = layoutVariant === 'search-first';
  // Pre-GW1 leads with the screenshot import, so it's the purple primary.
  const importLabel =
    importButtonText ||
    (isSearchFirst ? 'Import from screenshot' : 'Import my team');

  // The rating step hands a team FPL would not serve back here (FFH-2743). The
  // failure stays on the provider until the next import replaces it, so the
  // card survives the step change without any state of its own.
  const isTeamUnavailable = state.calculationFailure?.kind === 'team';

  // An empty search, or a team FPL would not serve, are the states where
  // screenshot import is promoted, so the payoff hint and the pitch above it
  // give up their room: the visitor has read them, and what they need now is
  // the way out.
  const isRecovering = isSearchFirst && (hasEmptyResults || isTeamUnavailable);

  const recoveryCopy = isTeamUnavailable
    ? {
        title: SEARCH_COPY.teamUnavailableCardTitle,
        body: SEARCH_COPY.teamUnavailableCardBody.replace(
          '{fplId}',
          state.fplId
        )
      }
    : {
        title: SEARCH_COPY.noResultsCardTitle,
        body: SEARCH_COPY.noResultsCardBody
      };

  const errorLine = inlineError && (
    <p
      className="mt-4 text-center font-body text-sm text-danger-600"
      data-cy="gytr-import-error"
    >
      {inlineError}
    </p>
  );

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept=".png,.jpg,.jpeg"
      onChange={handleFileInputChange}
      className="hidden"
    />
  );

  const idHelp = (
    <>
      <div className="mt-2.5 flex items-baseline justify-between gap-3">
        <p className="font-body text-xs leading-relaxed text-neutral-400">
          {SEARCH_COPY.idHint}
        </p>
        <button
          type="button"
          onClick={() => setIsIdHelpOpen((isOpen) => !isOpen)}
          aria-expanded={isIdHelpOpen}
          data-cy="gytr-id-help-toggle"
          className="shrink-0 font-body text-xs font-bold text-primary-500 transition-colors hover:text-primary-600"
        >
          {SEARCH_COPY.idHelpLink}
        </button>
      </div>
      {isIdHelpOpen && (
        <div
          data-cy="gytr-id-help"
          className="mt-3 rounded-xl border border-neutral-200 bg-white p-4 font-body text-[13px] leading-relaxed text-neutral-600"
        >
          <p>{SEARCH_COPY.idHelpBody}</p>
          <a
            href={SEARCH_COPY.idHelpLinkHref}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-block font-bold text-primary-500 transition-colors hover:text-primary-600"
          >
            {SEARCH_COPY.idHelpLinkLabel}
          </a>
        </div>
      )}
    </>
  );

  const searchSection = (
    <div className={`w-full ${isRecovering ? '' : 'mt-8'}`}>
      <label
        htmlFor={SEARCH_INPUT_ID}
        className="mb-2 block font-body text-[10px] font-bold uppercase tracking-widest text-neutral-500"
      >
        {SEARCH_COPY.fieldLabel}
      </label>
      <TeamSearch
        inputId={SEARCH_INPUT_ID}
        placeholder={SEARCH_COPY.placeholder}
        selectButtonText={searchSelectButtonText ?? 'Select'}
        ctaText={SEARCH_COPY.ctaText}
        // The card below carries the way forward once a search has come up
        // empty, so a second button competing with it is noise.
        isCtaHidden={isRecovering}
        inputFooter={isRecovering ? null : idHelp}
        onEmptyResultsChange={setHasEmptyResults}
        onSelect={handleEntrySelected}
      />
    </div>
  );

  // Both fallbacks as one line of text: reachable, and visibly not the path
  // being recommended.
  const fallbackLine = (
    <p className="mt-5 text-center font-body text-sm leading-relaxed text-neutral-500">
      {SEARCH_COPY.fallbackPrefix}
      <button
        type="button"
        onClick={openFilePicker}
        disabled={state.isImporting}
        data-cy="gytr-import-button"
        className="py-1 font-bold text-primary-500 transition-colors hover:text-primary-600 disabled:text-neutral-400"
      >
        {state.isImporting ? 'Reading your squad…' : SEARCH_COPY.fallbackImport}
      </button>
      {SEARCH_COPY.fallbackSeparator}
      <button
        type="button"
        onClick={onBuildManually}
        data-cy="gytr-build-manually-button"
        className="py-1 font-bold text-primary-500 transition-colors hover:text-primary-600"
      >
        {SEARCH_COPY.fallbackBuild}
      </button>
      {SEARCH_COPY.fallbackSuffix}
    </p>
  );

  const noResultsCard = (
    <div
      data-cy="gytr-no-results-card"
      className="mt-6 w-full rounded-xl border border-neutral-200 bg-white p-5"
    >
      <p className="font-heading text-base font-extrabold text-neutral-800">
        {recoveryCopy.title}
      </p>
      <p className="mt-1.5 font-body text-[13px] leading-relaxed text-neutral-600">
        {recoveryCopy.body}
      </p>
      <div className="mt-4">
        <CtaButton
          variant="secondary"
          onClick={openFilePicker}
          disabled={state.isImporting}
          dataCy="gytr-import-button"
        >
          {state.isImporting ? 'Reading your squad…' : importLabel}
        </CtaButton>
      </div>
      <button
        type="button"
        onClick={onBuildManually}
        data-cy="gytr-build-manually-button"
        className="mt-3.5 block w-full text-center font-body text-[13px] font-bold text-primary-500 transition-colors hover:text-primary-600"
      >
        {SEARCH_COPY.noResultsBuildLink}
      </button>
      {/* Kept with the control that launched the import, so a failed read is
          answered where the visitor is looking. */}
      {errorLine}
    </div>
  );

  const loginLink = (
    // Escape hatch for existing members bounced here from a protected page
    // (e.g. an onboarding email CTA) while logged out.
    <a
      href={loginUrl}
      data-cy="gytr-import-login-link"
      className="mt-6 block w-full text-center font-body text-sm font-bold text-primary-500 transition-colors hover:text-primary-600"
    >
      Already a member? Log in
    </a>
  );

  const proofBlock = proofLine && (
    <p className="mt-7 text-center font-body text-sm font-bold text-neutral-600">
      {proofLine}
    </p>
  );

  if (isSearchFirst) {
    return (
      <section className="flex items-start justify-center px-6 pb-16 pt-6">
        <div className="flex w-full max-w-sm flex-col items-center">
          {!isRecovering && (
            <>
              <RatingGauge value={null} size={SEARCH_LED_GAUGE_SIZE} />
              <StepHeader
                eyebrow={header?.eyebrow}
                accentHeading={header?.heading}
                subtext={header?.subtext}
                className="mt-6"
              />
            </>
          )}

          {searchSection}

          {isRecovering ? (
            noResultsCard
          ) : (
            <>
              {fallbackLine}
              {errorLine}
            </>
          )}

          {fileInput}
          {proofBlock}

          {!isRecovering && (
            <p className="mt-4 text-center font-body text-xs leading-relaxed text-neutral-400">
              {SEARCH_COPY.footnote}
            </p>
          )}

          {loginLink}
        </div>
      </section>
    );
  }

  return (
    <section className="flex items-start justify-center px-6 pb-16 pt-6">
      <div className="flex w-full max-w-sm flex-col items-center">
        <RatingGauge value={null} />

        <StepHeader
          eyebrow={header?.eyebrow}
          accentHeading={header?.heading}
          subtext={header?.subtext}
          className="mt-6"
        />

        <div className="mt-8 w-full space-y-3">
          <CtaButton
            variant="callout"
            onClick={openFilePicker}
            disabled={state.isImporting}
            dataCy="gytr-import-button"
          >
            {state.isImporting ? 'Reading your squad…' : importLabel}
          </CtaButton>
          <CtaButton
            variant="secondary"
            onClick={onBuildManually}
            dataCy="gytr-build-manually-button"
          >
            {buildManuallyText ?? 'Build manually'}
          </CtaButton>
          {fileInput}
        </div>

        {errorLine}

        {proofLine && (
          <p className="mt-6 text-center font-body text-sm font-bold text-neutral-600">
            {proofLine}
          </p>
        )}
        {footnote && (
          <p className="mt-4 text-center font-body text-xs leading-relaxed text-neutral-400">
            {footnote}
          </p>
        )}

        {loginLink}
      </div>
    </section>
  );
};

export default GytrImportStep;
